import React, { useState, useEffect, useContext } from "react";
import { PageLayout } from "../components/page-layout";
import { LeaderboardContext } from "../contexts/leaderboard-context";
import { getTeamAdminResource, setTeamAdminResource } from "../services/team-admin.service";
import { getAuctionListResource } from "../services/auction.service";
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Button } from '@mui/material';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import { useSiteVarsFunctions } from "../hooks/use-site-vars-functions";

export const TeamAdminPage = () => {
  const { leaderboardData } = useContext(LeaderboardContext);
  const [selectedClubId, setSelectedClubId] = useState("");
  const [teamData, setTeamData] = useState(null);
  const [playerSelections, setPlayerSelections] = useState({});
  const [allPlayers, setAllPlayers] = useState([]);
  const [auctionPlayers, setAuctionPlayers] = useState([]); // New state for auction players
  const { getTeamById, getPlayerPositionById } = useSiteVarsFunctions();

  useEffect(() => {
    const fetchAuctionPlayers = async () => {
      const { data, error } = await getAuctionListResource();
      if (data) {
        setAuctionPlayers(data.sort((a, b) => {
          if (a.positionId === b.positionId) {
            return a.lastName.localeCompare(b.lastName);
          }
          return a.positionId - b.positionId;
        }));
      } else if (error) {
        console.error("Error fetching auction players:", error);
      }
    };

    fetchAuctionPlayers();
  }, []);

  useEffect(() => {
    if (selectedClubId) {
      fetchTeamData(selectedClubId);
    }
  }, [selectedClubId]);

  const fetchTeamData = async (clubId) => {
    const { data, error } = await getTeamAdminResource(clubId);
    if (data) {
      data.players = data.players.filter(player => player.transfer !== "in")
      setTeamData(data);
      setAllPlayers(data.players); // Set all players from the API response
      const initialSelections = {};
      data.players.forEach((player) => {
        initialSelections[player.playerId] = player.playerId;
      });
      setPlayerSelections(initialSelections);
    } else if (error) {
      console.error("Error fetching team data:", error);
      setTeamData(null);
      setAllPlayers([]);
    }
  };

  const handleClubChange = (event) => {
    setSelectedClubId(event.target.value);
  };

  const handlePlayerChange = (originalPlayerId, event) => {
    setPlayerSelections((prevSelections) => ({
      ...prevSelections,
      [originalPlayerId]: parseInt(event.target.value),
    }));
  };

  const handleSave = async () => {
    if (!selectedClubId || !teamData) {
      alert("Please select a team first.");
      return;
    }

    const newPlayerIds = teamData.players.map(
      (player) => playerSelections[player.playerId]
    );

    const { data, error } = await setTeamAdminResource(
      selectedClubId,
      newPlayerIds
    );

    if (data) {
      alert("Team updated successfully!");
      fetchTeamData(selectedClubId); // Refresh data after save
    } else if (error) {
      console.error("Error saving team:", error);
      alert("Failed to save team. Please try again.");
    }
  };

  const getPlayersByPosition = (positionShort) => {
    if (!auctionPlayers) return [];
    return auctionPlayers;
    //return auctionPlayers.filter(player => getPlayerPositionById(player.positionId).name_short === positionShort);
  };

  return (
    <PageLayout>
      <Paper sx={{ padding: 1, marginTop: 1 }}>
      <div className="content-layout">
        <div className="content__body">
          <label htmlFor="team-select">Select Team:</label>
          <select id="team-select" onChange={handleClubChange} value={selectedClubId}>
            <option value="">--Please choose a team--</option>
            {leaderboardData.map((team) => (
              <option key={team.clubId} value={team.clubId}>
                {team.managerName} - {team.clubName}
              </option>
            ))}
          </select>

          {selectedClubId && !teamData && (
            <TableContainer component={Paper} sx={{ mt: 2 }}>
              <Stack spacing={1}>
                <Skeleton variant="rounded" width={"100%"} height={30} />
                <Skeleton variant="rounded" width={"100%"} height={45} />
                <Skeleton variant="rounded" width={"100%"} height={45} />
                <Skeleton variant="rounded" width={"100%"} height={45} />
                <Skeleton variant="rounded" width={"100%"} height={45} />
              </Stack>
            </TableContainer>
          )}

          {teamData && (
            <TableContainer component={Paper} sx={{ mt: 2 }}>
              <h2>Players for {leaderboardData.find(team => team.clubId === parseInt(selectedClubId))?.clubName}</h2>
              <Table sx={{ minWidth: 350 }} size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Position</TableCell>
                    <TableCell>Current Player</TableCell>
                    <TableCell>Replace With</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                    {teamData.players
                      .map((player) => (
                        <TableRow key={player.playerId}>
                          <TableCell>{getPlayerPositionById(player.positionId).name_short}</TableCell>
                          <TableCell>{player.lastName}, {player.firstName}</TableCell>
                          <TableCell>
                            <select
                              value={playerSelections[player.playerId] || ""}
                              onChange={(e) => handlePlayerChange(player.playerId, e)}
                            >
                              {getPlayersByPosition(getPlayerPositionById(player.positionId).name_short).map((p) => (
                                <option key={p.playerId} value={p.playerId}>
                                  {p.lastName}, {p.firstName} ({getTeamById(p.teamId).name_short}) - {getPlayerPositionById(p.positionId).name_short}
                                </option>
                              ))}
                            </select>
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
              </Table>
              <Button variant="contained" onClick={handleSave} sx={{ mt: 2 }}>Save Team</Button>
            </TableContainer>
          )}
        </div>
      </div>
      </Paper>
    </PageLayout>
  );
};